import type { TDropdownOption } from '@/types/dropdown'
import type { TTemplate, TTemplateList, TTemplateOrderOptions, TTemplatePlan, TTemplateSort } from '@/types/templates'

export const templateSortOptions: TDropdownOption[] = [
  { name: 'newest', label: 'Newest' },
  { name: 'popular', label: 'Most popular' },
]

export const templatePlanOptions: TDropdownOption[] = [
  { name: 'all', label: 'All' },
  { name: 'standard', label: 'Standard' },
  { name: 'pro', label: 'Pro' },
]

const mixSeed = 12250
const maxLockedInARow = 2

export function toTemplateSort(name?: string): TTemplateSort {
  return name === 'popular' ? 'popular' : 'newest'
}

export function toTemplatePlan(name?: string): TTemplatePlan {
  if (name === 'standard' || name === 'pro') {
    return name
  }

  return 'all'
}

export function seededRandom(seed: number): () => number {
  let state = seed >>> 0

  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let value = state
    value = Math.imul(value ^ (value >>> 15), value | 1)
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61)

    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function publishedTime(template: TTemplate): number {
  return Date.parse(template.published_at || template.created_at) || 0
}

function compareNewest(a: TTemplate, b: TTemplate): number {
  return publishedTime(b) - publishedTime(a)
}

function comparePopular(a: TTemplate, b: TTemplate): number {
  return (b.popular_users ?? 0) - (a.popular_users ?? 0) || compareNewest(a, b)
}

export function sortTemplatesBy(templates: TTemplateList, sort: TTemplateSort): TTemplateList {
  const compare = sort === 'popular' ? comparePopular : compareNewest

  return [...templates].sort(compare)
}

export function mixTemplates(
  available: TTemplateList,
  locked: TTemplateList,
  random: () => number = seededRandom(mixSeed),
): TTemplateList {
  const mixed: TTemplateList = []
  let availableIndex = 0
  let lockedIndex = 0
  let lockedInARow = 0

  while (availableIndex < available.length || lockedIndex < locked.length) {
    const availableLeft = availableIndex < available.length
    const lockedLeft = lockedIndex < locked.length
    const mustTakeAvailable = mixed.length === 0 || lockedInARow >= maxLockedInARow
    const takeLocked = lockedLeft && (!availableLeft || (!mustTakeAvailable && random() < 0.5))

    if (takeLocked) {
      mixed.push(locked[lockedIndex++]!)
      lockedInARow++
      continue
    }

    mixed.push(available[availableIndex++]!)
    lockedInARow = 0
  }

  return mixed
}

function mixGroup(templates: TTemplateList, isLocked: (template: TTemplate) => boolean): TTemplateList {
  const available = templates.filter((template) => !isLocked(template))
  const locked = templates.filter((template) => isLocked(template))

  return mixTemplates(available, locked)
}

export function orderTemplates(templates: TTemplateList, options: TTemplateOrderOptions): TTemplateList {
  const sorted = sortTemplatesBy(templates, options.sort)
  const matchesTheme = (template: TTemplate): boolean => template.tags.some((tag) => options.themeIds.includes(tag))
  const themed = options.themeIds.length ? sorted.filter(matchesTheme) : []
  const rest = options.themeIds.length ? sorted.filter((template) => !matchesTheme(template)) : sorted

  if (!options.mixLocked) {
    return [...themed, ...rest]
  }

  return [...mixGroup(themed, options.isLocked), ...mixGroup(rest, options.isLocked)]
}

export function matchesTemplatePlan(
  template: TTemplate,
  plan: TTemplatePlan,
  isPro: (template: TTemplate) => boolean,
): boolean {
  if (plan === 'all') {
    return true
  }

  return plan === 'pro' ? isPro(template) : !isPro(template)
}
